import { useState } from 'react';
import { RrcProfilePrefill } from '../RrcProfilePrefill';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  extractErrorMessage,
  useGetRrcCompanyProfileQuery,
  useGetCurrentRrcCompanyProfileByKeyQuery,
  useGetRrcCompanyProfilesByKeyQuery,
  useCreateRrcCompanyProfileMutation,
  useUpdateRrcCompanyProfileMutation,
  useDeleteRrcCompanyProfileMutation,
  type RrcCompanyProfile,
} from '@ots-frontend/ots-data';
import { useAuth } from '../../app/AuthProvider';
import { useApp, useAppDensity } from '../../app/AppContext';
import { OtsDialog } from '../../components/OtsDialog';
import { OtsThemeRoot } from '../../theme/OtsTheme';
import { AdminPage } from '../../components/AdminPage';
import { OtsActionButton } from '../../components/OtsButton';
import { OtsInlineAlert } from '../../components/OtsInlineAlert';
import { OtsDataTableEmpty } from '../../components/OtsDataTableCells';
import { ConfirmationDialog } from '../../dialogs/ConfirmationDialog';
import { RrcCompanyProfileView } from './RrcCompanyProfileView';
import { RrcCompanyProfileForm } from './RrcCompanyProfileForm';

export type RrcCompanyProfileInspectorProps = {
  id?: string;
  profileKey?: string;
  tenantCode?: string;
  readOnly?: boolean;
  initialMode?: 'create' | 'edit' | 'copy';
  defaults?: Partial<RrcCompanyProfile>;
  companyLocked?: boolean;
  presentation?: 'dialog' | 'embedded';
  compact?: boolean;
  onClose?: () => void;
  onChanged?: (profile?: RrcCompanyProfile) => void;
};
const Inspector = ({
  id,
  profileKey,
  tenantCode,
  readOnly: readOnlyProp = false,
  initialMode,
  defaults,
  companyLocked,
  presentation = 'dialog',
  compact: compactProp,
  onClose,
  onChanged,
}: RrcCompanyProfileInspectorProps) => {
  const auth = useAuth();
  const app = useApp();
  const density = useAppDensity();
  const compact = compactProp ?? density === 'compact';
  const [mode, setMode] = useState(initialMode);
  const [activeId, setActiveId] = useState(id);
  const [editing, setEditing] = useState<RrcCompanyProfile>();
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [pendingDelete, setPendingDelete] = useState<RrcCompanyProfile>();
  const [deleted, setDeleted] = useState(false);
  const canView = auth.hasAccess('rrccompanyprofile_view');
  const canCreate = auth.hasAccess('rrccompanyprofile_create');
  // The established mutation endpoints operate on the current tenant only. Explicitly scoped reads remain view-only.
  const readOnly = readOnlyProp || !!tenantCode;
  const byId = useGetRrcCompanyProfileQuery(
    canView && !deleted && activeId ? { id: activeId, tenantCode } : skipToken
  );
  const byKey = useGetCurrentRrcCompanyProfileByKeyQuery(
    canView && !deleted && !activeId && profileKey
      ? { profileKey, tenantCode }
      : skipToken
  );
  const query = activeId ? byId : byKey;
  const profile = query.currentData;
  const access = (value: RrcCompanyProfile, permission: string) =>
    auth.hasAccess(value, permission);
  const versions = useGetRrcCompanyProfilesByKeyQuery(
    canView && profile && !mode && !deleted
      ? { profileKey: profile.profileKey, tenantCode }
      : skipToken
  );
  const [create, creating] = useCreateRrcCompanyProfileMutation();
  const [update, updating] = useUpdateRrcCompanyProfileMutation();
  const [remove, removing] = useDeleteRrcCompanyProfileMutation();
  const busy = creating.isLoading || updating.isLoading || removing.isLoading;
  const openEditor = (value: RrcCompanyProfile, next: 'edit' | 'copy') => {
    if (readOnly) return;
    setEditing(value);
    setMode(next);
    setError(undefined);
    setNotice(undefined);
  };
  const closeEditor = () => {
    if (busy) return;
    if (initialMode) onClose?.();
    else {
      setMode(undefined);
      setEditing(undefined);
      setError(undefined);
    }
  };
  const save = async (payload: Partial<RrcCompanyProfile>) => {
    const target = editing ?? profile;
    if (
      busy ||
      readOnly ||
      (mode === 'edit'
        ? !target || !access(target, 'rrccompanyprofile_edit')
        : !canCreate)
    )
      return;
    setError(undefined);
    try {
      const saved = await (mode === 'edit'
        ? update(payload)
        : create(payload)
      ).unwrap();
      setActiveId(saved.id);
      setMode(undefined);
      setEditing(undefined);
      setNotice('Company profile saved.');
      onChanged?.(saved);
      if (initialMode) onClose?.();
    } catch (reason) {
      setError(
        extractErrorMessage(reason, 'The company profile could not be saved.')
      );
    }
  };
  const confirmDelete = async () => {
    if (
      !pendingDelete ||
      busy ||
      readOnly ||
      !access(pendingDelete, 'rrccompanyprofile_delete')
    )
      return;
    setError(undefined);
    try {
      await remove({ id: pendingDelete.id }).unwrap();
      setPendingDelete(undefined);
      setDeleted(true);
      onChanged?.();
      onClose?.();
    } catch (reason) {
      setError(
        extractErrorMessage(reason, 'The company profile could not be deleted.')
      );
    }
  };
  const title =
    mode === 'create'
      ? 'New RRC company profile'
      : mode === 'copy'
      ? 'Copy RRC company profile'
      : mode === 'edit'
      ? 'Edit RRC company profile'
      : 'RRC company profile';
  let content;
  if (deleted)
    content = <OtsInlineAlert tone="success" title="Company profile deleted" />;
  else if ((mode === 'create' && !canCreate) || (mode !== 'create' && !canView))
    content = (
      <OtsInlineAlert title="Access denied" tone="warning">
        You do not have permission to view or manage this company profile.
      </OtsInlineAlert>
    );
  else if (query.isFetching && !profile && (activeId || profileKey))
    content = (
      <OtsDataTableEmpty loading loadingText="Loading company profile…">
        {null}
      </OtsDataTableEmpty>
    );
  else if (query.error)
    content = (
      <OtsInlineAlert
        tone="danger"
        title="Company profile unavailable"
        action={
          <OtsActionButton size="compact" onClick={() => void query.refetch()}>
            Try again
          </OtsActionButton>
        }
      >
        {extractErrorMessage(
          query.error,
          'The company profile could not be loaded.'
        )}
      </OtsInlineAlert>
    );
  else if (profile && !access(profile, 'rrccompanyprofile_view'))
    content = (
      <OtsInlineAlert tone="warning" title="Access denied">
        You do not have permission to view this record.
      </OtsInlineAlert>
    );
  else if (
    mode &&
    (readOnly ||
      (mode === 'edit'
        ? !profile || !access(editing ?? profile, 'rrccompanyprofile_edit')
        : !canCreate))
  )
    content = (
      <OtsInlineAlert tone="warning" title="View only">
        This profile cannot be modified from this view.
      </OtsInlineAlert>
    );
  else if (mode)
    content = (
      <RrcCompanyProfileForm
        key={`${mode}-${editing?.id ?? profile?.id ?? 'new'}`}
        mode={mode}
        profile={editing ?? profile ?? defaults}
        companyLocked={companyLocked}
        regionId={app?.regionId}
        compact={compact}
        submitting={busy}
        error={error}
        onSubmit={(payload) => void save(payload)}
        onCancel={closeEditor}
      />
    );
  else if (!profile)
    content = (
      <OtsInlineAlert title="Company profile not found">
        Choose a company profile to inspect.
      </OtsInlineAlert>
    );
  else
    content = (
      <>
        {notice && <OtsInlineAlert tone="success" title={notice} />}
        {error && !pendingDelete && (
          <OtsInlineAlert tone="danger">{error}</OtsInlineAlert>
        )}
        <RrcCompanyProfileView
          key={profile.id}
          profile={profile}
          versions={(versions.currentData ?? []).filter((value) =>
            access(value, 'rrccompanyprofile_view')
          )}
          loadingVersions={versions.isFetching}
          versionsError={
            versions.error
              ? extractErrorMessage(
                  versions.error,
                  'Version history could not be loaded.'
                )
              : undefined
          }
          onRetry={() => void versions.refetch()}
          readOnly={readOnly}
          compact={compact}
          onCopy={canCreate ? (value) => openEditor(value, 'copy') : undefined}
          onEdit={(value) => openEditor(value, 'edit')}
          canEdit={(value) => access(value, 'rrccompanyprofile_edit')}
          onDelete={(value) => {
            setPendingDelete(value);
            setError(undefined);
          }}
          canDelete={(value) => access(value, 'rrccompanyprofile_delete')}
        />
      </>
    );
  const body = (
    <>
      {content}
      {pendingDelete && (
        <ConfirmationDialog
          open
          title="Delete company profile?"
          tone="danger"
          description={`Delete version ${pendingDelete.version} of ${
            pendingDelete.orgName ?? 'this company profile'
          }?`}
          confirmLabel="Delete profile"
          isConfirming={removing.isLoading}
          onConfirm={() => void confirmDelete()}
          onCancel={() => {
            if (!busy) {
              setPendingDelete(undefined);
              setError(undefined);
            }
          }}
          density={compact ? 'compact' : 'default'}
        >
          Referenced profiles may be protected from deletion by the server.
          {error && (
            <OtsInlineAlert className="mt-3" tone="danger">
              {error}
            </OtsInlineAlert>
          )}
        </ConfirmationDialog>
      )}
    </>
  );
  return presentation === 'dialog' ? (
    <OtsDialog
      open
      title={title}
      compact={compact}
      divided
      description={profile?.companyName ?? defaults?.companyName}
      onClose={() => {
        if (!busy) onClose?.();
      }}
    >
      {body}
    </OtsDialog>
  ) : (
    <OtsThemeRoot>
      <AdminPage
        title={title}
        fullWidth
        density={compact ? 'compact' : 'default'}
        actions={
          onClose && (
            <OtsActionButton onClick={onClose} disabled={busy} size="compact">
              Close
            </OtsActionButton>
          )
        }
      >
        <div className="mt-4">{body}</div>
      </AdminPage>
    </OtsThemeRoot>
  );
};

/** A new record or region gets its own draft; history/edit/copy never use live source defaults. */
export const RrcCompanyProfileInspector = (
  props: RrcCompanyProfileInspectorProps
) => {
  const app = useApp();
  const auth = useAuth();
  const id = props.defaults?.companyId;
  const type = 'COMPANY';
  const key = JSON.stringify([
    props.id,
    props.profileKey,
    props.tenantCode,
    props.initialMode,
    app?.regionId,
    type,
    id,
  ]);
  const sourcePermission = 'company_view';
  if (
    props.initialMode === 'create' &&
    !props.id &&
    !props.profileKey &&
    !props.tenantCode &&
    id &&
    app?.regionId &&
    auth.hasAccess('rrccompanyprofile_create') &&
    auth.hasAccess(sourcePermission)
  ) {
    return (
      <RrcProfilePrefill
        key={key}
        type={type}
        id={id}
        regionId={app.regionId}
        defaults={props.defaults}
        presentation={props.presentation}
        compact={props.compact}
        onClose={props.onClose}
      >
        {(draft) => <Inspector {...props} defaults={draft} />}
      </RrcProfilePrefill>
    );
  }
  return <Inspector key={key} {...props} />;
};
